<template>
  <div class="login-container page-content login-page">
    <video autoplay muted loop playsinline class="background-video">
      <source src="/LoginVideo.mp4" type="video/mp4" />
      Your browser does not support the video tag.
    </video>
    <v-card class="login-form" elevation="8" rounded="lg" max-width="400">
      <v-card-title class="text-center py-6" style="margin-top: -2rem;">
        <div style="font-size: 2rem; font-weight: 600; font-family: 'Orbitron', sans-serif; letter-spacing: 2px;">MODATO</div>
        <div style="font-size: 1.2rem; font-weight: normal; letter-spacing: 1px;">Admin Center</div>
      </v-card-title>
      <v-card-text>
        <v-form @submit.prevent="login">
          <v-text-field
            v-model="email"
            label="Email"
            prepend-inner-icon="mdi-email-outline"
            variant="outlined"
            density="compact"
            placeholder="Email address"
            autocomplete="email"
            required
            class="mb-4"
            :error-messages="emailError"
          ></v-text-field>
          <v-text-field
            v-model="password"
            :type="showPassword ? 'text' : 'password'"
            label="Password"
            prepend-inner-icon="mdi-lock-outline"
            :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
            variant="outlined"
            density="compact"
            placeholder="Enter your password"
            autocomplete="current-password"
            required
            @click:append-inner="showPassword = !showPassword"
            :error-messages="passwordError"
          ></v-text-field>
          <v-checkbox
            v-model="rememberMe"
            label="Remember Me"
            color="primary"
            hide-details
            class="mt--3"
          ></v-checkbox>
          <v-btn
            type="submit"
            color="primary"
            size="large"
           
            block
            class="mt-3"
            :loading="loading"
            style="background-color: #2C74B3; color: white; font-weight: bold;"
          >
            Login
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { toast } from 'vue3-toastify';
import secureAxios from '../utils/secureAxios';
import { jwtDecode } from 'jwt-decode';

// Fallback JWT decoder in case jwt-decode fails
function jwtDecodeFallback(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(payload);
  } catch (e) {
    return null;
  }
}

export default {
  name: 'LoginForm',
  data() {
    return {
      email: localStorage.getItem('rememberedEmail') || '',
      password: '',
      showPassword: false,
      rememberMe: !!localStorage.getItem('rememberedEmail'),
      loading: false,
      emailError: '',
      passwordError: '',
    };
  },
  mounted() {
    if (this.$route.query.sessionExpired === '1') {
      toast.warning('Your session expired. Please log in again to continue your work.', {
        autoClose: 6000,
      });
    }
  },
  methods: {
    async login() {
      this.emailError = '';
      this.passwordError = '';

      if (!this.email) {
        this.emailError = 'Email is required';
        return;
      }
      if (!this.password) {
        this.passwordError = 'Password is required';
        return;
      }

      this.loading = true;
      try {
        const clientTime = Math.floor(Date.now() / 1000);
        const response = await secureAxios.post('/api/users/login', {
          email: this.email,
          password: this.password,
          clientTime,
        }, {
          timeout: 10000,
        });

        const receivedToken = response.data.token;
        const receivedUser = response.data.user;

        if (!receivedToken) {
          toast.error('Login failed: No token received.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        if (!receivedUser) {
          toast.error('Login failed: No user data received.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        // Try using jwt-decode, fall back to custom decoder if it fails
        let decoded;
        try {
          decoded = jwtDecode(receivedToken);
        } catch (decodeError) {
          decoded = jwtDecodeFallback(receivedToken);
        }

        if (!decoded) {
          toast.error('Login failed: Invalid token.', { autoClose: 5000 });
          this.loading = false;
          return;
        }


        // Save to localStorage with error handling
        try {
          localStorage.setItem('authToken', receivedToken);
          const tokenAfterSet = localStorage.getItem('authToken');
          if (tokenAfterSet !== receivedToken) {
            toast.error('Failed to save authentication token.', { autoClose: 5000 });
            this.loading = false;
            return;
          }
        } catch (storageError) {
          console.error('Failed local auth storage:', storageError?.message || storageError);
          toast.error('Failed to save authentication token.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        try {
          const userString = JSON.stringify(receivedUser);
          localStorage.setItem('user', userString);
          const userAfterSet = localStorage.getItem('user');
          if (userAfterSet !== userString) {
            toast.error('Failed to save user data.', { autoClose: 5000 });
            this.loading = false;
            return;
          }
        } catch (storageError) {
          console.error('Failed local user storage:', storageError?.message || storageError);
          toast.error('Failed to save user data.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        try {
          if (this.rememberMe) {
            localStorage.setItem('rememberedEmail', this.email);
          } else {
            localStorage.removeItem('rememberedEmail');
          }
        } catch (storageError) {
          console.error('Failed local email-preference storage:', storageError?.message || storageError);
          toast.error('Failed to save email preference.', { autoClose: 5000 });
          this.loading = false;
          return;
        }

        this.$router.push(receivedUser.role === 'model' && receivedUser.model_id
          ? `/model/${receivedUser.model_id}/overview`
          : receivedUser.role === 'hr'
            ? '/admin-schedule'
            : receivedUser.role === 'accountant'
              ? '/card-expenses'
              : '/dashboard');
        toast.success('Logged in successfully', { autoClose: 3000 });
      } catch (error) {
        console.error('Login error:', error?.message || error);
        if (error.response) {
          if (error.response.status === 401) {
            toast.error('Invalid credentials', { autoClose: 5000 });
          } else if (error.response.status === 400) {
            error.response.data.errors?.forEach(err => {
              if (err.param === 'email') this.emailError = err.msg;
              if (err.param === 'password') this.passwordError = err.msg;
            });
          } else if (error.response.status === 429) {
            toast.error('Too many login attempts, please try again later', { autoClose: 5000 });
          } else {
            toast.error(`Login failed: ${error.response.data.message || 'Unknown error'}`, { autoClose: 5000 });
          }
        } else if (error.request) {
          toast.error('Network error: Unable to reach the server', { autoClose: 5000 });
        } else {
          toast.error(`Network error: ${error.message}`, { autoClose: 5000 });
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

 
<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;600&display=swap');

/* Container for centering the form */
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  margin: 0;
  padding: 0;
  background-color: transparent;
}

/* Style the login form */
.login-form {
  background: rgba(20, 20, 20, 0.95);
  padding: 2rem;
  color: white;
  max-width: 400px;
  width: 90%; /* Responsive width for smaller screens */
  margin: 1rem auto; /* Center horizontally */
  box-sizing: border-box;
}

/* Style inputs and labels */
.v-input input,
.v-input__control {
  color: white;
}

.v-label {
  color: rgba(255, 255, 255, 0.7);
}

.v-checkbox .v-label {
  color: rgba(255, 255, 255, 0.9);
}

.v-btn {
  color: white;
}

/* Video background styling */
.background-video {
  position: fixed;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  transform: translate(-50%, -50%);
  object-fit: cover;
  z-index: -1;
}

/* Ensure no default backgrounds interfere */
:global(html),
:global(body),
:global(#app),
:global(.v-application) {
  background-color: transparent !important;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

/* Handle very small screens */
@media (max-height: 600px) {
  .login-form {
    padding: 1.5rem;
    margin: 0.5rem auto;
  }
}

@media (max-width: 600px) {
  .login-form {
    width: 95%;
    margin: 0.5rem auto;
  }
}
</style>

<style>
input:-webkit-autofill,
input:-webkit-autofill:focus,
input:-webkit-autofill:hover,
input:-webkit-autofill:active {
  box-shadow: inset 0 0 0 1000px #141414 !important;
  -webkit-box-shadow: inset 0 0 0 1000px #141414 !important;
  -webkit-text-fill-color: white !important;
  caret-color: white !important;
  transition: background-color 9999s ease-in-out 0s !important;
}

.v-input input,
.v-field__input {
  background-color: transparent !important;
}
</style>
